/*
  Alegerea „persoana fizica / firma" din cos (vezi snippets/business-invoice.liquid).

  Face patru lucruri, toate optionale — fara JavaScript alegerea si campurile raman functionale,
  fiindca sunt in interiorul formularului de cos, iar aratarea campurilor e facuta in CSS cu `:has()`:

  1. Muta marcajul vizual pe optiunea aleasa si goleste campurile la revenirea pe „persoana fizica",
     ca sa nu ramana un CUI uitat pe o comanda care nu-l mai vrea.
  2. Valideaza CUI-ul cu cifra de control (algoritmul ANAF, cheia 753217532). Un CUI gresit ajunge
     altfel pe o factura fiscala, unde nu mai poate fi corectat prin editare.
  3. Salveaza imediat prin /cart/update.js. Cosul glisant are propriul buton de finalizare, care
     sare peste pagina de cos — fara salvarea asta, ce a scris clientul aici s-ar pierde.
  4. Blocheaza finalizarea comenzii pana cand clientul raspunde la intrebare, si apoi pe firma cu
     un CUI invalid sau fara denumire. Butonul e doar marcat blocat (`aria-disabled`), nu
     `disabled`: asa clicul ajunge tot la noi si putem spune DE CE nu merge, in loc sa lasam un
     buton mort. Fara JavaScript nu se blocheaza nimic — intrebarea redevine optionala.
  5. Tine sincronizate cele doua copii ale blocului (pagina de cos si cosul glisant), prin
     evenimentul `td:biz-change`. Fara asta, copia ramasa cu valorile vechi ar rescrie la
     urmatorul blur ce tocmai a salvat cealalta.
*/
(function () {
  'use strict';

  var KEY = '753217532';

  /* Cifra de control a CUI-ului: restul inmultit cu 10, modulo 11, cu 10 tratat ca 0. */
  function cuiValid(raw) {
    var digits = String(raw || '').replace(/^\s*RO/i, '').replace(/\D/g, '');
    if (digits.length < 2 || digits.length > 10) return false;
    var control = Number(digits.charAt(digits.length - 1));
    var body = digits.slice(0, -1);
    while (body.length < 9) body = '0' + body;
    var sum = 0;
    for (var i = 0; i < 9; i++) sum += Number(body.charAt(i)) * Number(KEY.charAt(i));
    var check = (sum * 10) % 11;
    if (check === 10) check = 0;
    return check === control;
  }

  function normalize(raw) {
    return String(raw || '').trim().replace(/\s+/g, '').toUpperCase();
  }

  class BusinessInvoice extends HTMLElement {
    connectedCallback() {
      this.types = Array.prototype.slice.call(this.querySelectorAll('[data-biz-type]'));
      this.cuiInput = this.querySelector('[data-biz-cui]');
      this.firmaInput = this.querySelector('[data-biz-firma]');
      this.statusEl = this.querySelector('[data-biz-status]');
      this.form = this.closest('form');
      if (!this.types.length || !this.cuiInput || !this.firmaInput) return;

      /* Butoanele de finalizare din acelasi formular; „Actualizeaza cosul" nu se blocheaza. */
      this.checkoutButtons = this.form
        ? Array.prototype.slice.call(this.form.querySelectorAll('[name="checkout"]'))
        : [];

      this.saved = {
        tip: this.selectedType(),
        cui: this.cuiInput.value,
        firma: this.firmaInput.value
      };

      this.types.forEach(function (input) {
        input.addEventListener('change', this.onTypeChange.bind(this));
      }, this);
      this.cuiInput.addEventListener('blur', this.onBlur.bind(this));
      this.firmaInput.addEventListener('blur', this.onBlur.bind(this));
      this.cuiInput.addEventListener('input', this.clearStatus.bind(this));

      if (this.form) this.form.addEventListener('submit', this.onSubmit.bind(this));
      this.syncGate();

      this.onExternal = this.applyExternal.bind(this);
      document.addEventListener('td:biz-change', this.onExternal);
    }

    disconnectedCallback() {
      if (this.onExternal) document.removeEventListener('td:biz-change', this.onExternal);
    }

    /* Blocul e randat de doua ori (pagina de cos + cosul glisant). Cine se schimba anunta. */
    broadcast(persisted) {
      document.dispatchEvent(new CustomEvent('td:biz-change', {
        detail: {
          tip: this.selectedType(),
          cui: this.isCompany() ? normalize(this.cuiInput.value) : '',
          firma: this.isCompany() ? this.firmaInput.value.trim() : '',
          persisted: !!persisted,
          source: this
        }
      }));
    }

    /* Cealalta copie a blocului s-a schimbat: preluam valorile, fara sa mai salvam si noi. */
    applyExternal(event) {
      var data = event.detail;
      if (!data || data.source === this) return;
      this.types.forEach(function (input) {
        input.checked = input.value === data.tip;
        var option = input.closest('.td-biz__option');
        if (option) option.classList.toggle('is-selected', input.checked);
      });
      this.cuiInput.value = data.cui;
      this.firmaInput.value = data.firma;
      /* Doar ce a ajuns pe cos conteaza drept „salvat"; altfel am sari peste salvarea noastra. */
      if (data.persisted) this.saved = { tip: data.tip, cui: data.cui, firma: data.firma };
      this.clearStatus();
      this.syncGate();
    }

    /* Sirul gol inseamna „clientul nu a raspuns inca", nu „persoana fizica". */
    selectedType() {
      for (var i = 0; i < this.types.length; i++) {
        if (this.types[i].checked) return this.types[i].value;
      }
      return '';
    }

    /* Butonul de finalizare arata blocat cat timp intrebarea nu are raspuns. */
    syncGate() {
      var blocked = !this.selectedType();
      this.classList.toggle('is-unanswered', blocked);
      this.checkoutButtons.forEach(function (button) {
        button.classList.toggle('is-blocked', blocked);
        if (blocked) button.setAttribute('aria-disabled', 'true');
        else button.removeAttribute('aria-disabled');
      });
    }

    isCompany() {
      return this.selectedType() === 'Firma';
    }

    onTypeChange() {
      this.types.forEach(function (input) {
        input.closest('.td-biz__option').classList.toggle('is-selected', input.checked);
      });
      this.clearStatus();
      this.syncGate();
      if (this.isCompany()) {
        /* Inca nu avem ce salva (CUI-ul lipseste), dar cealalta copie trebuie sa stie alegerea. */
        this.broadcast(false);
        this.firmaInput.focus();
        return;
      }
      /* Inapoi pe persoana fizica: campurile se golesc, altfel ar ramane un CUI care nu mai e cerut. */
      this.cuiInput.value = '';
      this.firmaInput.value = '';
      this.save();
    }

    /* Ce e gresit acum, ca text. Sirul gol inseamna „se poate trimite". */
    problem() {
      if (!this.selectedType()) return 'Alege întâi cum facturăm comanda: persoană fizică sau firmă.';
      if (!this.isCompany()) return '';
      var cui = normalize(this.cuiInput.value);
      var firma = this.firmaInput.value.trim();
      if (!cui && !firma) return 'Completează denumirea firmei și CUI-ul, sau alege „Persoană fizică".';
      if (cui && !cuiValid(cui)) return 'CUI-ul nu pare valid. Verifică cifrele — îl trecem exact așa pe factură.';
      if (!firma) return 'Mai lipsește denumirea firmei.';
      if (!cui) return 'Mai lipsește CUI-ul. Fără el nu putem emite factura pe firmă.';
      return '';
    }

    onBlur() {
      var issue = this.problem();
      /* Cat timp clientul completeaza, campurile goale nu sunt inca o eroare. */
      if (issue && (this.cuiInput.value.trim() || this.firmaInput.value.trim())) {
        this.setStatus(issue, true);
        return;
      }
      if (issue) return;
      this.cuiInput.value = normalize(this.cuiInput.value);
      this.save();
    }

    clearStatus() {
      this.setStatus('', false);
    }

    setStatus(text, isError) {
      if (!this.statusEl) return;
      this.statusEl.textContent = text;
      this.statusEl.classList.toggle('is-error', !!isError);
    }

    save() {
      var tip = this.selectedType();
      if (!tip) return;
      var cui = this.isCompany() ? normalize(this.cuiInput.value) : '';
      var firma = this.isCompany() ? this.firmaInput.value.trim() : '';
      if (tip === this.saved.tip && cui === this.saved.cui && firma === this.saved.firma) return;

      var self = this;
      fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        /* Sirul gol sterge atributul — asa se curata o comanda marcata din greseala pe firma. */
        body: JSON.stringify({ attributes: { Facturare: tip, CUI: cui, Firma: firma } })
      })
        .then(function (r) {
          if (!r.ok) throw new Error(r.status);
          self.saved = { tip: tip, cui: cui, firma: firma };
          self.setStatus(cui ? 'Salvat. Factura va fi emisă pe ' + firma + '.' : '', false);
          self.broadcast(true);
        })
        .catch(function () {
          /* Nu e o pierdere: campurile sunt in formularul de cos si se trimit oricum la submit. */
          self.setStatus('Nu am putut salva acum — datele se trimit oricum la finalizarea comenzii.', false);
        });
    }

    onSubmit(event) {
      /* „Actualizeaza cosul" e tot un submit al aceluiasi formular, dar nu duce la plata. */
      if (event.submitter && event.submitter.name === 'update') return;

      var issue = this.problem();
      if (!issue) return;
      event.preventDefault();
      this.setStatus(issue, true);

      /* Fara raspuns la intrebare nu are rost sa trimitem focusul in campuri: sunt ascunse. */
      if (!this.selectedType()) {
        this.scrollIntoView({ block: 'center', behavior: 'smooth' });
        if (this.types[0]) this.types[0].focus();
        return;
      }
      var target = this.firmaInput.value.trim() ? this.cuiInput : this.firmaInput;
      target.focus();
      target.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
  }

  if (!customElements.get('business-invoice')) {
    customElements.define('business-invoice', BusinessInvoice);
  }
})();
